import React, { useState, useEffect } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import {
    ThumbsUp,
    ThumbsDown,
    Filter,
    Calendar,
    ArrowRight,
    ArrowLeft,
    MessageSquare
} from "lucide-react";
import { getAllFeedbacks } from "../../../api";

const Feedback = () => {
    const { isDarkTheme } = useTheme();
    const [activeTab, setActiveTab] = useState("all");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [userId, setUserId] = useState("");
    const [feedbackData, setFeedbackData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    // Trigger fade-in animation on component mount
    useEffect(() => {
        setFadeIn(true);
    }, []);

    // Trigger animation when changing tabs
    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 150);
        return () => clearTimeout(timer);
    }, [activeTab]);

    // Trigger animation on component mount and fetch feedback data
    useEffect(() => {
        const fetchData = async () => {
            const data = await getAllFeedbacks();
            setFeedbackData(data.feedbacks_list);
            setFadeIn(true);
        };
      
        fetchData();
      }, []);

    // Apply filters based on type, userId and date range
    useEffect(() => {
        if (feedbackData.length === 0) return;  // If there's no data, don't filter yet
        
        let result = feedbackData;

        // Filter by feedback type
        if (activeTab !== "all") {
            result = result.filter(item => item.type === activeTab);
        }

        // Filter by userId if provided
        if (userId) {
            result = result.filter(item =>
                item.userId.toLowerCase().includes(userId.toLowerCase()) ||
                item.user.toLowerCase().includes(userId.toLowerCase())
            );
        }

        // Filter by date range if provided
        if (dateFrom) {
            result = result.filter(item => new Date(item.date) >= new Date(dateFrom));
        }

        if (dateTo) {
            result = result.filter(item => new Date(item.date) <= new Date(dateTo));
        }

        setFilteredData(result);
    }, [activeTab, userId, dateFrom, dateTo, feedbackData]);

    // Apply filters function
    const applyFilters = () => {
        // Filters are already applied through useEffect
        setIsFilterOpen(false);
    };

    // Reset filters function
    const resetFilters = () => {
        setUserId("");
        setDateFrom("");
        setDateTo("");
    };

    // Quick stats for the dashboard
    const positiveCount = feedbackData.filter(item => item.type === "positive").length;
    const negativeCount = feedbackData.filter(item => item.type === "negative").length;
    const positivePercentage = Math.round((positiveCount / feedbackData.length) * 100);

    return (
        <div className={`w-full transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
            {/* Header with stats cards */}
            <div className="flex flex-col mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                            AI Response Feedback
                        </h2>
                        <p className={`text-sm mt-1 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                            Track user sentiment on chatbot responses
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        {/* Filter Button with active state */}
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className={`px-4 py-2 rounded-lg flex items-center transition-all duration-200 ${isFilterOpen
                                ? isDarkTheme
                                    ? "bg-amber-600 text-white"
                                    : "bg-amber-500 text-white"
                                : isDarkTheme
                                    ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700"
                                    : "bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 hover:border-gray-300"
                                } shadow-sm`}
                        >
                            <Filter size={18} className="mr-2" />
                            Filter
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Total Feedback Card */}
                    <div className={`p-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${isDarkTheme ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                        } border`}>
                        <div className="flex justify-between items-center">
                            <div>
                                <p className={`text-sm font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                    Total Responses
                                </p>
                                <p className="text-2xl font-bold mt-1">{feedbackData.length}</p>
                            </div>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkTheme ? "bg-gray-700" : "bg-amber-100"
                                }`}>
                                <MessageSquare size={20} className={`${isDarkTheme ? "text-amber-400" : "text-amber-600"}`} />
                            </div>
                        </div>
                    </div>

                    {/* Positive Feedback Card */}
                    <div className={`p-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${isDarkTheme ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                        } border`}>
                        <div className="flex justify-between items-center">
                            <div>
                                <p className={`text-sm font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                    Liked Responses
                                </p>
                                <p className="text-2xl font-bold mt-1 text-green-500">{positiveCount}</p>
                                <div className="flex items-center mt-1">
                                    <span className={`text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                        {positivePercentage}% like ratio
                                    </span>
                                </div>
                            </div>
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
                                <ThumbsUp size={20} className="text-green-500" />
                            </div>
                        </div>
                    </div>

                    {/* Negative Feedback Card */}
                    <div className={`p-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${isDarkTheme ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                        } border`}>
                        <div className="flex justify-between items-center">
                            <div>
                                <p className={`text-sm font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                    Disliked Responses
                                </p>
                                <p className="text-2xl font-bold mt-1 text-red-500">{negativeCount}</p>
                                <div className="flex items-center mt-1">
                                    <span className={`text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                        {100 - positivePercentage}% dislike ratio
                                    </span>
                                </div>
                            </div>
                            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-100">
                                <ThumbsDown size={20} className="text-red-500" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter dropdown (conditionally rendered) */}
            {isFilterOpen && (
                <div className={`p-4 mb-6 rounded-lg transition-all duration-300 ${isDarkTheme ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                    } border shadow-md`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>
                                Date Range
                            </label>
                            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                                <div className="relative flex-1">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <Calendar size={16} className="text-gray-500 " />
                                    </div>
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        className={`pl-10 pr-4 py-2 w-full rounded-lg ${isDarkTheme
                                            ? "bg-gray-700 border-gray-600"
                                            : "bg-gray-50 border-gray-300"
                                            } border focus:outline-none focus:ring-1 focus:ring-amber-500`}
                                    />
                                </div>
                                <span className={`${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>to</span>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className={`px-4 py-2 flex-1 rounded-lg ${isDarkTheme
                                        ? "bg-gray-700 border-gray-600"
                                        : "bg-gray-50 border-gray-300"
                                        } border focus:outline-none focus:ring-1 focus:ring-amber-500`}
                                />
                            </div>
                        </div>
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>
                                User ID/Name
                            </label>
                            <input
                                type="text"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                className={`px-4 py-2 w-full rounded-lg ${isDarkTheme
                                    ? "bg-gray-700 border-gray-600"
                                    : "bg-gray-50 border-gray-300"
                                    } border focus:outline-none focus:ring-1 focus:ring-amber-500`}
                                placeholder="Filter by user ID or name"
                            />
                        </div>
                        <div className="flex items-end space-x-2">
                            <button
                                onClick={applyFilters}
                                className={`px-4 py-2 rounded-lg flex-1 transition-colors duration-200 ${isDarkTheme
                                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                                    : "bg-amber-500 hover:bg-amber-600 text-white"
                                    }`}
                            >
                                Apply Filters
                            </button>
                            <button
                                onClick={resetFilters}
                                className={`px-4 py-2 rounded-lg transition-colors duration-200 ${isDarkTheme
                                    ? "bg-gray-700 hover:bg-gray-600 text-gray-300"
                                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                                    }`}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback Type Tabs with animations - Fixed for small screens */}
            <div className="overflow-x-auto">
                <div className="flex mb-6 border-b border-gray-200 dark:border-gray-700 relative min-w-max">
                    <button
                        onClick={() => setActiveTab("all")}
                        className={`py-3 px-6 transition-all duration-200 relative ${activeTab === "all"
                            ? isDarkTheme
                                ? "text-amber-500 font-medium"
                                : "text-amber-600 font-medium"
                            : isDarkTheme
                                ? "text-gray-400 hover:text-gray-300"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        All Responses
                        {activeTab === "all" && (
                            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-amber-600"></span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab("positive")}
                        className={`py-3 px-6 flex items-center transition-all duration-200 relative ${activeTab === "positive"
                            ? isDarkTheme
                                ? "text-amber-500 font-medium"
                                : "text-amber-600 font-medium"
                            : isDarkTheme
                                ? "text-gray-400 hover:text-gray-300"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        <ThumbsUp size={16} className="mr-2" />
                        Liked
                        {activeTab === "positive" && (
                            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-amber-600"></span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab("negative")}
                        className={`py-3 px-6 flex items-center transition-all duration-200 relative ${activeTab === "negative"
                            ? isDarkTheme
                                ? "text-amber-500 font-medium"
                                : "text-amber-600 font-medium"
                            : isDarkTheme
                                ? "text-gray-400 hover:text-gray-300"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        <ThumbsDown size={16} className="mr-2" />
                        Disliked
                        {activeTab === "negative" && (
                            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400 to-amber-600"></span>
                        )}
                    </button>
                </div>
            </div>

            {/* Feedback Cards - Updated for simple chatbot feedback */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredData.length > 0 ? (
                    filteredData.map(feedback => (
                        <div
                            key={feedback.id}
                            className={`p-4 rounded-lg ${isDarkTheme ? "bg-gray-800" : "bg-white"
                                } shadow-sm border transition-all duration-300 ${feedback.type === "positive"
                                    ? isDarkTheme
                                        ? "border-green-800"
                                        : "border-green-200"
                                    : isDarkTheme
                                        ? "border-red-800"
                                        : "border-red-200"
                                }`}
                        >
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${feedback.type === "positive"
                                        ? "bg-green-100 text-green-600"
                                        : "bg-red-100 text-red-600"
                                        }`}>
                                        {feedback.avatar}
                                    </div>
                                    <div className="ml-3">
                                        <p className="font-medium">{feedback.user}</p>
                                        <p className={`text-xs ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                            ID: {feedback.userId}
                                        </p>
                                    </div>
                                </div>
                                <div className={`rounded-full p-2 flex items-center justify-center ${feedback.type === "positive"
                                    ? "bg-green-100"
                                    : "bg-red-100"
                                    }`}>
                                    {feedback.type === "positive" ? (
                                        <ThumbsUp size={18} className="text-green-600" />
                                    ) : (
                                        <ThumbsDown size={18} className="text-red-600" />
                                    )}
                                </div>
                            </div>

                            {/* User query */}
                            <div className={`text-sm mb-3 p-3 rounded-lg ${isDarkTheme ? "bg-gray-700" : "bg-gray-50"
                                }`}>
                                <p className={`font-medium text-xs mb-1 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                                    User Query:
                                </p>
                                <p className={`${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>{feedback.queryText}</p>
                            </div>

                            {/* AI Response */}
                            <div className={`text-sm p-3 rounded-lg mb-3 ${isDarkTheme
                                ? "bg-amber-900/20 border border-amber-800/30"
                                : "bg-amber-50 border border-amber-100"
                                }`}>
                                <div className="flex items-center mb-1">
                                    <span className={`text-xs font-medium flex items-center ${isDarkTheme ? "text-amber-400" : "text-amber-600"
                                        }`}>
                                        <MessageSquare size={12} className="mr-1" />
                                        AI Response:
                                    </span>
                                </div>
                                <p className={`${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>
                                    {feedback.responseText}
                                </p>
                            </div>

                            {/* Date */}
                            <div className="flex justify-end items-center mt-3 text-xs">
                                <span className={`${isDarkTheme ? "text-gray-400" : "text-gray-500"} flex items-center`}>
                                    <Calendar size={12} className="mr-1" />
                                    {feedback.date}
                                </span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={`col-span-2 p-8 text-center rounded-lg ${isDarkTheme ? "bg-gray-800 text-gray-300" : "bg-white text-gray-700"
                        } border ${isDarkTheme ? "border-gray-700" : "border-gray-200"}`}>
                        <p>No feedback matches your current filters. Try adjusting your criteria.</p>
                    </div>
                )}
            </div>

            {/* Enhanced Pagination */}
            <div className="flex items-center justify-between mt-6">
                <div className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                    Showing 1 to {filteredData.length} of {filteredData.length} results
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        className={`px-3 py-1.5 rounded-lg flex items-center transition-colors duration-200 ${isDarkTheme
                            ? "bg-gray-800 text-gray-400 border border-gray-700"
                            : "bg-white text-gray-500 border border-gray-300"
                            }`}
                        disabled
                    >
                        <ArrowLeft size={16} className="mr-1" />
                        Previous
                    </button>
                    <button
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-200 ${isDarkTheme ? "bg-amber-600 text-white" : "bg-amber-500 text-white"
                            }`}
                    >
                        1
                    </button>
                    <button
                        className={`px-3 py-1.5 rounded-lg flex items-center transition-colors duration-200 ${isDarkTheme
                            ? "bg-gray-800 text-gray-400 border border-gray-700"
                            : "bg-white text-gray-500 border border-gray-300"
                            }`}
                        disabled
                    >
                        Next
                        <ArrowRight size={16} className="ml-1" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Feedback;